
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import { REFRESH_CONFIG } from '@/constant/index'
import { refreshManager } from '@/utils/refreshManager'
import { getCurrentUserInfo } from '@/utils/request'
import { applyDemoModeByUserInfo, isDemoMode, getDemoDashboardData } from '@/utils/demoMode'

export default defineComponent({
  name: 'App',
  /** 初始化刷新生命周期和提示状态，并提供微前端布局标记以区分宿主高度与独立视口。 */
  setup() {
    const store = useStore()
    const showRefreshTip = ref(false)
    let demoModeTimer: ReturnType<typeof setInterval> | null = null

    /**
     * 根据主应用下发的 user_info 判断演示模式
     * 演示模式仅使用 mock/json/test.json 代替台账接口
     */
    const initDemoModeByUserInfo = () => {
      const tryApply = () => {
        const userInfo = getCurrentUserInfo()
        if (!userInfo?.username && !userInfo?.id) return false

        const enabled = applyDemoModeByUserInfo(userInfo)
        if (enabled) {
          // 业务分布率等：由 test.json 推导，不走主应用接口
          store.dispatch('parentData/setDashboardData', getDemoDashboardData())
          console.info('[演示模式] username=15516751561，台账/业务分布率来自 test.json，跳过业务接口请求')
        }
        return true
      }

      if (tryApply()) return

      let retries = 0
      demoModeTimer = setInterval(() => {
        retries += 1
        if (tryApply() || retries >= 50) {
          if (demoModeTimer) {
            clearInterval(demoModeTimer)
            demoModeTimer = null
          }
        }
      }, 100)
    }
    
    // 刷新所有组件数据
    const refreshAllComponents = async () => {
      try {
        // 显示刷新提示
        if (REFRESH_CONFIG.SHOW_REFRESH_LOADING) {
          showRefreshTip.value = true
        }

        if (isDemoMode()) {
          store.dispatch('parentData/setDashboardData', getDemoDashboardData())
        }
        
        await store.dispatch('websocket/refreshAllComponents')
        
        // 隐藏刷新提示
        if (REFRESH_CONFIG.SHOW_REFRESH_LOADING) {
          setTimeout(() => {
            showRefreshTip.value = false
          }, 1000)
        }
      } catch (error) {
        console.error('定时刷新失败:', error)
        // 隐藏刷新提示
        if (REFRESH_CONFIG.SHOW_REFRESH_LOADING) {
          showRefreshTip.value = false
        }
      }
    }
    
    // 页面可见性变化处理
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 页面变为可见时，确保刷新管理器正在运行
        if (!refreshManager.isRefreshRunning() && REFRESH_CONFIG.ENABLE_AUTO_REFRESH) {
          refreshManager.start()
        }
      } else {
        // 页面不可见时，刷新管理器会自动跳过刷新
      }
    }
    
    // 生命周期钩子
    onMounted(() => {
      initDemoModeByUserInfo()

      // 设置刷新回调
      refreshManager.setRefreshCallback(refreshAllComponents)
      
      // 启动定时刷新
      if (REFRESH_CONFIG.ENABLE_AUTO_REFRESH) {
        refreshManager.start()
      }
      
      // 监听页面可见性变化
      document.addEventListener('visibilitychange', handleVisibilityChange)
    })
    
    onUnmounted(() => {
      if (demoModeTimer) {
        clearInterval(demoModeTimer)
        demoModeTimer = null
      }
      refreshManager.stop()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    })
    
    return {
      showRefreshTip,
      // 嵌入后台时使用宿主分配的高度；独立打开时仍占满浏览器。
      isEmbedded: !!(window as any).__POWERED_BY_QIANKUN__,
    }
  }
})
